import {
  HRefArguments,
  ProjectNavbarLink,
  ProjectNavbarPageId,
  ProjectNavbarSubpageId,
  TopLevelProjectNavbarLink,
} from "@/components/navigation/ProjectNavbarCommon.constants";
import {
  CATEGORY_NAV_LINKS,
  CATEGORY_NAV_RELOCATED_SETTINGS_PANES,
} from "@/components/navigation/category-nav.constants";
import { PROJECT_NAVBAR_LINKS } from "@/components/navigation/ProjectNavbar.constants";
import { useProjectNavbarCounts } from "@/components/navigation/useNavbarCounts";
import { useNavLinkEnvironment } from "@/components/navigation/nav-link-environment";
import { NavbarMainItem } from "@/components/navigation/navigation.types";
import { useKubePodsCrashing } from "@/components/settings/kubernetes/KubeCrashWarning";
import { useSettingPanes } from "@/components/settings/settings.hooks";
import { useRouteAndTeamInfo } from "@/utils/hooks/url/use-route-info";
import { useCategoryNavEnabled } from "@/utils/hooks/use-category-nav-enabled";
import { useExplorerUiDisabled } from "@/utils/hooks/use-explorer-ui-disabled";
import { useFlagsV2 } from "@/utils/hooks/use-flags-grpc";
import { useDANGEROUSisChalkEmployee } from "@/utils/hooks/use-is-chalk-employee";
import { useIsServerlessEnvironment } from "@/utils/hooks/use-is-serverless-environment";
import { isServerlessEnvironmentPB } from "@/utils/environment";
import { pluralize } from "@/utils/string-utils";
import { useCallback, useMemo } from "react";

type LinkType = TopLevelProjectNavbarLink<ProjectNavbarPageId, ProjectNavbarSubpageId>;

export const useProjectNavbarLinks = (): NavbarMainItem<HRefArguments>[] => {
  const {
    activeDeploymentId,
    environment: routeEnvironment,
    environmentId,
  } = useRouteAndTeamInfo();
  const linkEnvironment = useNavLinkEnvironment();
  // The environment the links lead into, which a global settings page names apart from its URL.
  const environment = environmentId != null ? routeEnvironment : linkEnvironment?.environment;
  const flagsV2 = useFlagsV2();
  const { explorerUiDisabled } = useExplorerUiDisabled();
  const { badPods } = useKubePodsCrashing(environmentId);
  const deploymentWarningPodCount = badPods.length;
  const canSeeAdminLinks = useDANGEROUSisChalkEmployee();
  const routeServerless = useIsServerlessEnvironment();
  const { isServerless, loading: serverlessLoading } =
    environmentId == null && environment != null
      ? { isServerless: isServerlessEnvironmentPB(environment), loading: false }
      : routeServerless;
  const { paneGroups: settingsPaneGroups } = useSettingPanes({ currentPaneId: null });
  const categoryNavEnabled = useCategoryNavEnabled();

  // All count badges come from the bulk navbar-counts RPC, keyed by navbar id.
  const subpageCounts = useProjectNavbarCounts();
  const visibleSettingsPaneIds = useMemo(() => {
    return new Set(settingsPaneGroups.flatMap((group) => group.panes.map((pane) => pane.id)));
  }, [settingsPaneGroups]);

  const hasEnabledFlag = useCallback(
    (link: ProjectNavbarLink<string>): boolean => {
      if (link.flag == null) {
        return false;
      }

      return (
        flagsV2.enabled(link.flag) ||
        (link.additionalFlags?.some((flag) => flagsV2.enabled(flag)) ?? false)
      );
    },
    [flagsV2],
  );

  const isFlaggedOffButVisibleInAdminMode = useCallback(
    (link: ProjectNavbarLink<string>): boolean => {
      return link.flag != null && canSeeAdminLinks && !hasEnabledFlag(link);
    },
    [canSeeAdminLinks, hasEnabledFlag],
  );

  // The SQL explorer is gated by the inverted `disable_explorer_ui` flag rather than a
  // per-link `flag` (enabled-semantics) field, so it needs its own admin-treatment check.
  const isExplorerLinkHiddenButVisibleInAdminMode = useCallback(
    (id: string): boolean => {
      return id === "explorer-sql" && explorerUiDisabled && canSeeAdminLinks;
    },
    [canSeeAdminLinks, explorerUiDisabled],
  );

  const isEnvironmentResourceMissing = useCallback(
    (link: ProjectNavbarLink<string>): boolean =>
      link.environmentResourceIdField != null && !environment?.[link.environmentResourceIdField],
    [environment],
  );

  // Serverless gating reuses the existing `admin` machinery: once we know an environment is
  // serverless, a serverless-flagged link is treated exactly like an admin-only link (hidden
  // from customers by `filterSubPages`/`shouldShowLink`, shown to chalk admins with the admin
  // treatment). Until we know (loading), we drop the link so it is never briefly exposed. On a
  // team-load error `isServerless` is false, so we intentionally treat it as hosted and show
  // the link normally — the destination pages guard themselves and surface the error there.
  const gateServerlessItem = useCallback(
    <T extends ProjectNavbarLink<string>>(rawItem: T): T | null => {
      const item =
        isServerless && rawItem.serverlessHref != null
          ? { ...rawItem, href: rawItem.serverlessHref }
          : rawItem;
      if (!item.serverless) {
        return item;
      }
      if (serverlessLoading) {
        return null;
      }
      if (!isServerless) {
        return item;
      }
      return { ...item, admin: true };
    },
    [isServerless, serverlessLoading],
  );

  const applyServerlessGating = useCallback(
    (page: LinkType): LinkType | null => {
      const gatedPage = gateServerlessItem(page);
      if (gatedPage == null) {
        return null;
      }
      return {
        ...gatedPage,
        subpages: gatedPage.subpages
          .map((subPage) => gateServerlessItem(subPage))
          .filter(
            (subPage): subPage is ProjectNavbarLink<ProjectNavbarSubpageId> => subPage != null,
          ),
      };
    },
    [gateServerlessItem],
  );

  // The SQL item has no `flag` (a top-level page in the classic nav, a Compute row in the category
  // nav): disable_explorer_ui hides it, though chalk employees keep it (with the admin treatment
  // applied in addAdminModeStyling); otherwise the sql_explorer_disabled flag does.
  const isSqlExplorerVisible = useCallback((): boolean => {
    if (explorerUiDisabled) {
      return canSeeAdminLinks;
    }
    return flagsV2.ready && !flagsV2.enabled("sql_explorer_disabled");
  }, [canSeeAdminLinks, explorerUiDisabled, flagsV2]);

  const shouldShowLink = useCallback(
    (link: LinkType): boolean => {
      if (link.admin && !canSeeAdminLinks) {
        return false;
      }
      if (link.flag != null) {
        return hasEnabledFlag(link) || (!link.strictFlag && canSeeAdminLinks);
      }
      if (link.id === "explorer-sql") {
        return isSqlExplorerVisible();
      }
      return true;
    },
    [canSeeAdminLinks, hasEnabledFlag, isSqlExplorerVisible],
  );

  const filterSubPages = useCallback(
    (page: LinkType): LinkType => {
      const subpages = page.subpages?.filter((subPage) => {
        if (subPage.id === "data-lineage" && !activeDeploymentId) {
          return false;
        }
        // The relocated panes' parent ids are category nav rows; the classic nav's Integrations
        // shares an id with one, and lists its Vector store row by its own rules.
        const relocatedPane =
          categoryNavEnabled && CATEGORY_NAV_RELOCATED_SETTINGS_PANES.get(subPage.id) === page.id;
        if (page.id === "settings" || relocatedPane) {
          // Settings visibility has its own rules, also for panes other rows list, but a pane for an
          // environment resource the environment lacks is still hidden from customers, as below.
          return (
            visibleSettingsPaneIds.has(subPage.id) &&
            !(isEnvironmentResourceMissing(subPage) && !canSeeAdminLinks)
          );
        }

        if (subPage.admin && !canSeeAdminLinks) {
          return false;
        }
        if (subPage.id === "explorer-sql") {
          return isSqlExplorerVisible();
        }
        if (isEnvironmentResourceMissing(subPage) && !canSeeAdminLinks) {
          return false;
        }
        return (
          subPage.flag == null ||
          hasEnabledFlag(subPage) ||
          (!subPage.strictFlag && canSeeAdminLinks)
        );
      });

      return {
        ...page,
        subpages,
      };
    },
    [
      activeDeploymentId,
      canSeeAdminLinks,
      categoryNavEnabled,
      hasEnabledFlag,
      isEnvironmentResourceMissing,
      isSqlExplorerVisible,
      visibleSettingsPaneIds,
    ],
  );

  const addAdminModeStyling = useCallback(
    (page: LinkType): LinkType => {
      return {
        ...page,
        admin:
          page.admin ||
          isFlaggedOffButVisibleInAdminMode(page) ||
          isExplorerLinkHiddenButVisibleInAdminMode(page.id),
        subpages: page.subpages?.map((subPage) => ({
          ...subPage,
          admin:
            subPage.admin ||
            isFlaggedOffButVisibleInAdminMode(subPage) ||
            (isEnvironmentResourceMissing(subPage) && canSeeAdminLinks) ||
            isExplorerLinkHiddenButVisibleInAdminMode(subPage.id),
        })),
      };
    },
    [
      canSeeAdminLinks,
      isEnvironmentResourceMissing,
      isExplorerLinkHiddenButVisibleInAdminMode,
      isFlaggedOffButVisibleInAdminMode,
    ],
  );

  const addCounts = useCallback(
    (page: LinkType): LinkType => {
      // A row with subpages renders a submenu chevron where the badge would go, so only a row
      // without them can carry its own count. The category nav counts only visible subpages,
      // since a row whose subpages are all hidden renders no chevron.
      const showsOwnCount = categoryNavEnabled
        ? page.subpages.every((subPage) => subPage.hideFromNavigation)
        : page.subpages.length === 0;
      if (showsOwnCount) {
        const count = subpageCounts[page.id];
        return count == null ? page : { ...page, count };
      }

      let changed = false;
      const subpages = page.subpages.map((subPage) => {
        const count = subpageCounts[subPage.id];
        if (count == null) return subPage;
        changed = true;
        return { ...subPage, count };
      });

      return changed ? { ...page, subpages } : page;
    },
    [categoryNavEnabled, subpageCounts],
  );

  return useMemo(
    () =>
      (categoryNavEnabled ? CATEGORY_NAV_LINKS : PROJECT_NAVBAR_LINKS)
        .map(applyServerlessGating)
        .filter((page): page is LinkType => page != null)
        .filter(shouldShowLink)
        .flatMap((page): NavbarMainItem<HRefArguments>[] => {
          const pageWithVisibleSubpages = filterSubPages(page);
          // In the category nav a section whose pages are all hidden would otherwise render as a
          // bare row linking to a page it no longer lists.
          if (
            categoryNavEnabled &&
            page.subpages.length > 0 &&
            pageWithVisibleSubpages.subpages.length === 0
          ) {
            return [];
          }
          const pageWithFilteredSubpages = addAdminModeStyling(addCounts(pageWithVisibleSubpages));
          if (page.id === "deployments" && deploymentWarningPodCount > 0) {
            return [
              {
                ...pageWithFilteredSubpages,
                subpages: pageWithFilteredSubpages.subpages?.map((subpage) =>
                  subpage.id === "all-deployments" ? { ...subpage, visualTone: "danger" } : subpage,
                ),
                tooltipContent: `${deploymentWarningPodCount} ${pluralize(
                  deploymentWarningPodCount,
                  "pod",
                  "pods",
                )} with errors`,
                icon: "redStatusDot",
                visualTone: "danger",
              },
            ];
          }

          return [pageWithFilteredSubpages];
        }),
    [
      addAdminModeStyling,
      addCounts,
      applyServerlessGating,
      categoryNavEnabled,
      deploymentWarningPodCount,
      filterSubPages,
      shouldShowLink,
    ],
  );
};
